import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../Api';
import { TIPOS_DIA, TIPOS_NAO_LETIVOS, mapaNaoLetivos, chaveUTC } from '../NonSchoolDays';
import { nomePeriodoDoRegime } from '../../utils/regimeDoAno';

/**
 * Calendário da escola SÓ PARA VISUALIZAR (portal do responsável).
 * Mostra, mês a mês, os dias letivos e os feriados, recessos e outros dias sem aula
 * cadastrados nos períodos do ano, além do início e fim de cada período.
 */

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const SEMANA = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const ENDPOINTS = ['/index/istQuarter', '/index/iindQuarter', '/index/iii_rdQuarter', '/index/iv_thQuarter'];
const QTD = { BIMESTRAL: 4, TRIMESTRAL: 3, SEMESTRAL: 2 };
const pad = (n) => String(n).padStart(2, '0');
const hojeStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const dataBR = (k) => k.split('-').reverse().join('/');

const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(15, 30, 40, .5)', backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)', zIndex: 1000, display: 'flex', alignItems: 'center',
    justifyContent: 'center', padding: 12,
};
const caixa = {
    background: 'var(--surface, #fff)', color: 'var(--text, #16323b)', borderRadius: 14,
    width: '100%', maxWidth: 920, maxHeight: '92vh', display: 'flex', flexDirection: 'column',
    boxShadow: '0 20px 50px rgba(0,0,0,.25)', overflow: 'hidden',
};

export default function CalendarioEscolar({ open, onClose, idSchool, nomeEscola }) {
    const [ano, setAno] = useState(null);
    const [periodos, setPeriodos] = useState([]);
    const [regime, setRegime] = useState('');
    const [loading, setLoading] = useState(false);
    const [erro, setErro] = useState('');

    useEffect(() => {
        if (!open || !idSchool) return undefined;
        let ativo = true;
        (async () => {
            setLoading(true);
            setErro('');
            try {
                const sy = await api.post('/get/schoolYear', { idSchool });
                const year = sy?.data?.data;
                if (!ativo) return;
                setAno(year);
                const res = await Promise.all(ENDPOINTS.map((url) =>
                    api.post(url, { year, id_school: idSchool }).then((r) => r?.data?.data?.[0] || null).catch(() => null)
                ));
                if (!ativo) return;
                const reg = String(res[0]?.assessmentRegime || '').toUpperCase();
                setRegime(reg);
                setPeriodos(res.slice(0, QTD[reg] || 4).map((q, i) => (q ? { ...q, numero: i + 1 } : null)).filter(Boolean));
            } catch (e) {
                if (ativo) setErro('Não foi possível carregar o calendário da escola.');
            } finally {
                if (ativo) setLoading(false);
            }
        })();
        return () => { ativo = false; };
    }, [open, idSchool]);

    const dados = useMemo(() => {
        const letivos = new Set();
        const naoLetivosLista = [];
        const inicioFim = new Map(); // chave -> texto
        const nomeP = nomePeriodoDoRegime(regime);
        periodos.forEach((p) => {
            (p.schoolDays || []).forEach((d) => { const k = chaveUTC(d.date); if (k) letivos.add(k); });
            (p.nonSchoolDays || []).forEach((d) => naoLetivosLista.push(d));
            if (p.startyear) inicioFim.set(`${p.startyear}-${pad(p.startmonth)}-${pad(p.startday)}`, `Início do ${p.numero}º ${nomeP.toLowerCase()}`);
            if (p.endyear) inicioFim.set(`${p.endyear}-${pad(p.endmonth)}-${pad(p.endday)}`, `Fim do ${p.numero}º ${nomeP.toLowerCase()}`);
        });
        const naoLetivos = mapaNaoLetivos(naoLetivosLista);
        // meses do ano letivo: do início do 1º ao fim do último período
        const chaves = [...letivos, ...naoLetivos.keys(), ...inicioFim.keys()].sort();
        const meses = [];
        if (chaves.length) {
            let [y, m] = chaves[0].split('-').map(Number);
            const [yf, mf] = chaves[chaves.length - 1].split('-').map(Number);
            while (y < yf || (y === yf && m <= mf)) {
                meses.push({ y, m: m - 1 });
                m += 1; if (m > 12) { m = 1; y += 1; }
            }
        }
        const lista = [...naoLetivos.entries()].sort(([a], [b]) => a.localeCompare(b));
        return { letivos, naoLetivos, inicioFim, meses, lista };
    }, [periodos, regime]);

    if (!open) return null;
    const hoje = hojeStr();

    return (
        <div style={overlay} onClick={onClose}>
            <div style={caixa} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Calendário da escola">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: '1px solid var(--line, #e3e8eb)' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: 17 }}>📅 Calendário escolar {ano || ''}</div>
                        {nomeEscola && <div style={{ fontSize: 13, color: 'var(--text-muted, #5b6b72)' }}>{nomeEscola}</div>}
                    </div>
                    <button type="button" onClick={onClose} aria-label="Fechar" style={{ border: 'none', background: 'transparent', fontSize: 22, cursor: 'pointer', color: 'var(--text, #16323b)' }}>×</button>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', padding: '10px 16px', fontSize: 12.5, borderBottom: '1px solid var(--line, #e3e8eb)' }}>
                    <Leg cor={TIPOS_DIA.letivo.cor} txt="Dia letivo (tem aula)" />
                    {TIPOS_NAO_LETIVOS.map((t) => <Leg key={t} cor={TIPOS_DIA[t].cor} txt={TIPOS_DIA[t].label} />)}
                    <Leg cor="transparent" borda="#2563eb" txt="Início/fim de período" />
                    <Leg cor="transparent" borda="#16323b" txt="Hoje" grossa />
                </div>

                <div style={{ overflowY: 'auto', padding: 16 }}>
                    {loading && <div style={{ textAlign: 'center', padding: 30 }}>Carregando calendário…</div>}
                    {!loading && erro && <div style={{ color: '#dc2626' }}>{erro}</div>}
                    {!loading && !erro && dados.meses.length === 0 && (
                        <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted, #5b6b72)' }}>
                            A escola ainda não cadastrou o calendário deste ano.
                        </div>
                    )}

                    {!loading && !erro && dados.meses.length > 0 && (
                        <>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 14 }}>
                                {dados.meses.map(({ y, m }) => {
                                    const primeiro = new Date(y, m, 1).getDay();
                                    const dias = new Date(y, m + 1, 0).getDate();
                                    const celulas = [];
                                    for (let i = 0; i < primeiro; i++) celulas.push(<span key={`v${i}`} />);
                                    for (let d = 1; d <= dias; d++) {
                                        const k = `${y}-${pad(m + 1)}-${pad(d)}`;
                                        const marca = dados.naoLetivos.get(k);
                                        const tipo = marca ? TIPOS_DIA[marca.type] : (dados.letivos.has(k) ? TIPOS_DIA.letivo : null);
                                        const marco = dados.inicioFim.get(k);
                                        const titulo = [dataBR(k), marca ? `${TIPOS_DIA[marca.type].label}${marca.description ? `: ${marca.description}` : ''}` : (tipo ? 'Dia letivo' : 'Sem aula'), marco].filter(Boolean).join(' — ');
                                        celulas.push(
                                            <span key={d} title={titulo} style={{
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', height: 28, borderRadius: 7,
                                                fontSize: 12.5, fontWeight: tipo ? 700 : 500, cursor: 'default',
                                                background: tipo ? tipo.cor : 'transparent',
                                                color: tipo ? '#fff' : 'var(--text-muted, #8a979d)',
                                                boxShadow: [marco ? 'inset 0 0 0 2px #2563eb' : '', k === hoje ? '0 0 0 2px var(--text, #16323b)' : ''].filter(Boolean).join(', ') || 'none',
                                            }}>{d}</span>
                                        );
                                    }
                                    const qtdLetivos = [...dados.letivos].filter((k) => k.startsWith(`${y}-${pad(m + 1)}-`)).length;
                                    return (
                                        <div key={`${y}-${m}`} style={{ border: '1px solid var(--line, #e3e8eb)', borderRadius: 12, padding: 10 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 800, marginBottom: 6 }}>
                                                <span>{MESES[m]} {y}</span>
                                                <span style={{ fontSize: 11, fontWeight: 700, color: '#0d9f6e' }}>{qtdLetivos} {qtdLetivos === 1 ? 'dia letivo' : 'dias letivos'}</span>
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3, fontSize: 11, fontWeight: 700, textAlign: 'center', color: 'var(--text-muted, #5b6b72)', marginBottom: 3 }}>
                                                {SEMANA.map((s, i) => <span key={i}>{s}</span>)}
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3 }}>{celulas}</div>
                                        </div>
                                    );
                                })}
                            </div>

                            {dados.lista.length > 0 && (
                                <div style={{ marginTop: 18 }}>
                                    <div style={{ fontWeight: 800, marginBottom: 8 }}>Dias sem aula</div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        {dados.lista.map(([k, marca]) => (
                                            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                                                <span style={{ width: 12, height: 12, borderRadius: 3, background: TIPOS_DIA[marca.type].cor, flexShrink: 0 }} />
                                                <strong style={{ minWidth: 86 }}>{dataBR(k)}</strong>
                                                <span>{TIPOS_DIA[marca.type].label}{marca.description ? ` — ${marca.description}` : ''}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function Leg({ cor, txt, borda, grossa }) {
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{
                width: 13, height: 13, borderRadius: 4, background: cor, flexShrink: 0,
                border: borda ? `${grossa ? 2 : 2}px solid ${borda}` : 'none', boxSizing: 'border-box',
            }} />
            {txt}
        </span>
    );
}
